// =============================================================================
// utils/embed.ts — Tour embedding whitelist: normalization + matching.
//
// DEPENDENCY-FREE on purpose: this module is bundled into BOTH the SPA (client
// embed check) AND the Vercel edge middleware (CSP frame-ancestors). Do not
// import anything that references `window`/`document` or Vite `@` aliases here,
// or the edge bundle breaks. Client-only helpers (embedder origin) live in
// `utils/iframe.ts`.
//
// Semantics (matches the editor copy "Leave empty to allow embedding on any
// domain"): an EMPTY whitelist means the tour can be embedded anywhere; a
// NON-EMPTY whitelist restricts embedding to those origins (plus 'self').
// =============================================================================

/**
 * Normalize a user-entered domain/origin into a CSP `frame-ancestors` source.
 * Accepts `example.com`, `https://example.com`, `www.example.com`,
 * `*.example.com`, `https://*.example.com`, `localhost`, `localhost:5173`.
 * Returns a scheme-qualified source (`https://example.com`) or `null` if the
 * input can't be turned into a safe source.
 */
export function normalizeEmbedSource(input: string): string | null {
  let v = (input ?? "").trim().toLowerCase();
  if (!v) return null;

  // Strip scheme and any path/query/fragment — we only keep host[:port].
  v = v.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  if (!v) return null;

  // localhost (optionally with a port) — useful for owner previews/dev.
  if (/^localhost(:\d{1,5})?$/.test(v)) return `http://${v}`;

  // host, optionally with a leading wildcard label and an optional port.
  if (!/^(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}(:\d{1,5})?$/.test(v)) return null;
  return `https://${v}`;
}

/**
 * Build the `frame-ancestors` CSP directive from a tour's whitelist.
 * Empty/invalid list → `frame-ancestors *` (embed anywhere, per product copy).
 * Non-empty → `frame-ancestors 'self' <sources…>`.
 */
export function buildFrameAncestors(list: string[] | null | undefined): string {
  if (!list || list.length === 0) return "frame-ancestors *";
  const sources = list
    .map(normalizeEmbedSource)
    .filter((s): s is string => s !== null);
  if (sources.length === 0) return "frame-ancestors *";
  return `frame-ancestors 'self' ${sources.join(" ")}`;
}

/**
 * True when `embedderOrigin` (e.g. `https://foo.com`) is permitted by `list`.
 * Wildcard sources (`*.example.com`) match the bare apex and any subdomain.
 */
export function isEmbedOriginAllowed(
  embedderOrigin: string,
  list: string[],
): boolean {
  const origin = (embedderOrigin ?? "").trim().toLowerCase().replace(/\/$/, "");
  if (!origin) return false;

  for (const raw of list) {
    const src = normalizeEmbedSource(raw);
    if (!src) continue;
    const host = src.replace(/^https?:\/\//, ""); // host[:port]
    const originHost = origin.replace(/^https?:\/\//, "");

    if (host.startsWith("*.")) {
      const base = host.slice(2);
      if (originHost === base || originHost.endsWith(`.${base}`)) return true;
    } else if (originHost === host) {
      return true;
    }
  }
  return false;
}
